import React, { FunctionComponent, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { AccessibilityInfo, Keyboard } from 'react-native'

import BottomSheetGorhom from '@gorhom/bottom-sheet'
import { isEqual, isNil, noop } from 'lodash-es'
import styled, { css, useTheme } from 'styled-components/native'

import { ContentAttributes } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/customContentAttributes'
import { useGetLinkedAssignmentsPages } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/hooks/useGetLinkedAssignmentsPages'
import { useGetSchemaTotalPages } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/hooks/useGetSchemaTotalPages'
import { ActivityResponseData } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/types'
import {
  getHiddenConditionalPageNumbers,
  getLastPageFromResponse,
  getResumePageFromResponse,
} from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { useIsMounted } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/useIsMounted'
import { usePrevious } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/usePrevious'
import { useWindowSize } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/useWindowSize'
import { Assignment, Contentmetadata } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { CountryValues } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

import { AvatarDetails } from '../../atoms/icons/Avatar'
import { LightbulbIcon } from '../../atoms/icons/LightbulbIcon'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { ThemedStatusBar } from '../../atoms/themedStatusBar/ThemedStatusBar'
import { IS_WEB } from '../../constants'
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback'
import { useKeyboardVisible } from '../../hooks/useKeyboardVisible'
import {
  ACTIVITY_EXIT_MODAL_CONTENT,
  ActivityExitModalContent,
} from '../../molecules/activityExitModalContent/ActivityExitModalContent'
import { CareOnboardingHeader } from '../../molecules/careOnboardingHeader/CareOnboardingHeader'
import { InlineInfoBanner } from '../../molecules/inlineInfoBanner/InlineInfoBanner'
import { InputAccessory } from '../../molecules/inputAccessory/InputAccessory'
import { ActivityHeader } from '../../organisms/activityHeader/ActivityHeader'
import {
  FormBody,
  FormBodyCustomStyles,
  FormButtonParams,
  FormChangeProperties,
} from '../../organisms/formBody/FormBody'
import { Modal } from '../../organisms/modal/Modal'
import { colors } from '../../styles/colors'
import { getAuroraAwareBackgroundColor } from '../../styles/commonStyles'
import { Flex1View } from '../../templates/content/CommonViews'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

import type { UICoreFormContext } from '../../organisms/formBody/types'

export enum ActivityExitType {
  SAVE_DRAFT = 'save_draft',
  EXIT_WITHOUT_SAVING = 'exit_without_saving',
}

export interface ActivityEntryProps {
  a11yFocusedElementOnLoad?: MutableRefObject<any>
  activityIntroductionProviderInfo?: Dict
  avatarDetails?: AvatarDetails
  childFirstName?: string
  clientFirstName?: string
  contentMetadata?: Contentmetadata
  contentAttributes?: ContentAttributes
  context?: UICoreFormContext
  customerCountryList?: CountryValues[]
  deleteDraft: (percentProgress: number) => void
  disableAutoSave?: boolean
  disableIsRequiredFormValidation?: boolean
  editIndex?: number
  exit: (values?: Dict, exitType?: ActivityExitType) => void
  focused: boolean
  instructions?: string | null
  isProviderDraft?: boolean
  isProviderPreview?: boolean
  linkedAssignments?: Assignment[]
  locale?: string
  name?: string
  newEntry?: boolean
  onInit?: (percentProgress: number, currentPage?: number) => void
  onPageChange?: (currentPage: number) => void
  startFromFirstPage?: boolean
  onPopOverPress?: (link: string) => void
  onSavePress?: (percentProgress: number, currentPage?: number) => void
  preRequisiteAssignment?: Assignment
  isRenewalCareConsent?: boolean
  response?: Dict
  responseDate?: string
  resumeAtFirstPageWithErrors?: boolean
  saveForm: (args: { values: Dict; formSubmit?: boolean }) => void
  setAssessmentMaxPage?: (maxPage: number) => void
  showAllPages?: boolean
  showCurrentPageOnly?: boolean
  title?: string
  useCustomizedActivityIntroduction?: boolean
  userCountryIsoCode?: string
  videoSessionOpen?: boolean
  withModal?: boolean
  withPageBreaks?: boolean
  sentryLogMissingUISchemaKeys?: (keys: string) => void
  onBackPress?: (currentPage: number) => void
  setCancelPendingDraftSaves?: (value: null | (() => void)) => void
  modalHasCloseButton?: boolean
  logDiagnostics?: (data: Record<string, unknown>) => void
  inlineInfoBannerMessage?: React.ReactNode
  inlineInfoBannerIcon?: React.ReactElement
  connectedServicePrefillValues?: Record<string, number>
  showUpdatePrepopulatedFormBanner?: boolean
}

const AUTOSAVE_DEBOUNCE_MS = 3000
/**
 * A wrapper for FormBody that controls pagination and renders the activity header
 */

const ActivityEntryContent = styled(Flex1View)<{
  theme: ThemeType
  backgroundColor: string
  videoSessionOpen: boolean
}>(({ theme: { isAuroraTheme }, backgroundColor, videoSessionOpen }) => ({
  backgroundColor: getAuroraAwareBackgroundColor(isAuroraTheme, backgroundColor),
  // Fixed positioning required on web to display correctly in Safari on mobile and maintain
  // footer at bottom of screen while loading.
  ...(IS_WEB && {
    position: 'fixed',
    top: videoSessionOpen ? '90px' : '0',
    left: '0',
    right: '0',
    bottom: '0',
  }),
}))

const CTAContainer = styled.View<{ theme: ThemeType; videoSessionOpen: boolean }>(
  ({
    theme: {
      spacing,
      breakpoints: { isMinWidthTablet },
    },
    videoSessionOpen,
  }) => ({
    marginTop: spacing['16px'],
    marginBottom: isMinWidthTablet ? spacing['16px'] : spacing['32px'],
    ...(IS_WEB &&
      videoSessionOpen && {
        paddingBottom: '90px',
        borderTopColor: colors.ui_oatmeal3,
      }),
  }),
)

export const ActivityEntry: FunctionComponent<ActivityEntryProps> = ({
  a11yFocusedElementOnLoad,
  activityIntroductionProviderInfo,
  avatarDetails,
  childFirstName,
  clientFirstName,
  contentMetadata = {},
  contentAttributes = {},
  context,
  customerCountryList,
  deleteDraft,
  disableAutoSave = false,
  disableIsRequiredFormValidation,
  editIndex,
  exit,
  focused,
  instructions,
  isProviderDraft = false,
  isProviderPreview = false,
  linkedAssignments = [],
  isRenewalCareConsent = false,
  locale,
  name,
  newEntry,
  startFromFirstPage = false,
  onInit = noop,
  onPageChange,
  onPopOverPress,
  onSavePress = noop,
  preRequisiteAssignment,
  response,
  responseDate,
  resumeAtFirstPageWithErrors = false,
  saveForm,
  setAssessmentMaxPage,
  showAllPages,
  showCurrentPageOnly = false,
  title = '',
  useCustomizedActivityIntroduction = false,
  userCountryIsoCode,
  videoSessionOpen = false,
  withModal = true,
  withPageBreaks = true,
  sentryLogMissingUISchemaKeys,
  onBackPress,
  setCancelPendingDraftSaves = noop,
  modalHasCloseButton = true,
  logDiagnostics,
  inlineInfoBannerMessage,
  inlineInfoBannerIcon,
  connectedServicePrefillValues,
  showUpdatePrepopulatedFormBanner = false,
}) => {
  const { colors, isAuroraTheme } = useTheme() as ThemeType
  const [isAtTopOfPage, setIsAtTopOfPage] = useState(true)
  const [initialValues, setInitialValues] = useState<ActivityResponseData | Dict | null>(null)
  const [lastPage, setLastPage] = useState(0)
  const [showModal, setShowModal] = useState(false)
  const initialized = useRef(false)
  const previousInitialized = usePrevious(initialized.current)
  const formValues = useRef<Dict | null>(null)
  const isMounted = useIsMounted()
  const bottomSheetRef = useRef<BottomSheetGorhom>(null)
  const closeBottomSheet = useCallback(
    (exitActivity?: boolean, exitType?: ActivityExitType) => {
      bottomSheetRef?.current?.close()
      if (exitActivity) {
        exit(undefined, exitType)
      }
    },
    [exit],
  )
  const { schema, uiSchema, initialValues: initialMetaDataValues, withConditionalLastPages } = contentMetadata ?? {}
  const inputAccessoryViewID = 'ActivityEntry'
  const [headerHeight, setHeaderHeight] = useState(0)
  const [footerHeight, setFooterHeight] = useState(0)
  const [, windowHeight] = useWindowSize()
  const backgroundColor = colors.backgroundSecondary
  const keyboardVisible = useKeyboardVisible()

  const {
    bypassReview = false,
    careOnboardingTitle,
    customActivityIntroduction,
    customErrorMessage,
    enableBackNavigation = false,
    hideProgressBar,
    hideSubmitButton = false,
    // If initialPageIfHasPrereq = 1, this means that the instructions page (page 0)
    // should be skipped if the user completed a prerequisite assignment (to the current assigment).
    // initialPageIfHasPrereq = 0, means that the instructions page should not be skipped, even
    // if the user completed the prerequisite assignment(s).
    initialPageIfHasPrereq = 0,
    linkedAssignmentTitle,
    maxWidthSubmitButton = false,
    showFullProgressOnSubmit = false,
    singlePageContent = false,
  } = contentAttributes

  const skipInstructionsPageIfHasPrereq = !!preRequisiteAssignment && initialPageIfHasPrereq > 0

  const activityTitle = isRenewalCareConsent ? title : (linkedAssignmentTitle ?? title)
  const hasPostLinkedAssignment =
    linkedAssignments.findIndex((linkedAssignment) => linkedAssignment.content.name === name) !==
    linkedAssignments.length - 1

  // calculate the total number of pages in the schema
  const schemaTotalPages = useGetSchemaTotalPages({ schema, uiSchema })
  const { totalPages: linkedAssignmentsTotalPages, currentPage: linkedAssignmentsCurrentPage } =
    useGetLinkedAssignmentsPages({
      selectedAssignmentName: name,
      linkedAssignments,
    })

  const introOffset = 1
  const lastPageOffset = 2
  const [totalPages, setTotalPages] = useState<number>(schemaTotalPages)
  const [hiddenPages, setHiddenPages] = useState<Set<number>>(new Set())
  const previousAnswers = useRef<Set<number> | null>(null)
  const resumePageIndex = skipInstructionsPageIfHasPrereq
    ? Math.max(getLastPageFromResponse({ response, schema }), initialPageIfHasPrereq)
    : 0
  const firstPageIndex = skipInstructionsPageIfHasPrereq ? initialPageIfHasPrereq : 0

  const [currentPage, setCurrentPage] = useState<number>(
    startFromFirstPage ? firstPageIndex : showAllPages ? totalPages : resumePageIndex,
  )
  const [showErrorBanner, setShowErrorBanner] = useState<boolean>(false)
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)

  const isLastPage = currentPage === totalPages || singlePageContent
  const percentProgress =
    showFullProgressOnSubmit && isSubmitted
      ? 1
      : ((singlePageContent ? totalPages : currentPage) + introOffset + linkedAssignmentsCurrentPage) /
        (totalPages + lastPageOffset + linkedAssignmentsTotalPages)
  const previousPercentProgress = usePrevious(percentProgress)
  const intl = useIntl()
  const { formatMessage } = intl
  const bypassReviewButtonText = hasPostLinkedAssignment
    ? formatMessage({
        defaultMessage: 'Submit and Continue',
        description: 'button to proceed to submit activity and continue to next activity',
      })
    : formatMessage({
        defaultMessage: 'Submit',
        description: 'button to submit activity and continue to next activity',
      })

  const bannerMessage =
    inlineInfoBannerMessage ||
    (showUpdatePrepopulatedFormBanner
      ? formatMessage({
          defaultMessage: 'Please review and update all of your intake information.',
          description: 'Inline banner shown when client intake form is prefilled from a prior visit',
        })
      : undefined)
  const bannerIcon =
    inlineInfoBannerIcon ||
    (showUpdatePrepopulatedFormBanner ? (
      <LightbulbIcon size={22} fillColor={colors.components.inlineInfoBanner.infoIcon.fill} />
    ) : undefined)

  let InstructionsSection

  if (skipInstructionsPageIfHasPrereq) {
    InstructionsSection = <></>
  } else if (customActivityIntroduction === 'CareOnboardingHeader' && !isRenewalCareConsent) {
    InstructionsSection = (
      <CareOnboardingHeader
        childFirstName={childFirstName}
        clientFirstName={clientFirstName}
        avatarDetails={avatarDetails}
        activityTitle={activityTitle}
        customTitle={careOnboardingTitle}
      />
    )
  }

  // on mount set initial values equal to response for existing responses
  useEffect(() => {
    if (isNil(initialValues)) {
      if (!newEntry && !isNil(response)) {
        setInitialValues(response)
        if (schema) {
          const lastPageFromResponse = getLastPageFromResponse({ response, schema })
          setLastPage(lastPageFromResponse)
          if (!showAllPages && lastPageFromResponse && !startFromFirstPage) {
            const resumePage = resumeAtFirstPageWithErrors
              ? getResumePageFromResponse({ response, schema, totalPages: schemaTotalPages })
              : getLastPageFromResponse({ response, schema })
            setCurrentPage(Math.max(resumePage, resumePageIndex))
          } else if (startFromFirstPage && skipInstructionsPageIfHasPrereq) {
            setCurrentPage(firstPageIndex)
          }
        }
      } else {
        setInitialValues({ ...(initialMetaDataValues || {}), ...connectedServicePrefillValues })
      }
      initialized.current = true
    }
  }, [
    initialMetaDataValues,
    resumePageIndex,
    firstPageIndex,
    initialValues,
    lastPage,
    newEntry,
    response,
    resumeAtFirstPageWithErrors,
    schema,
    showAllPages,
    schemaTotalPages,
    skipInstructionsPageIfHasPrereq,
    connectedServicePrefillValues,
    startFromFirstPage,
  ])

  useEffect(() => {
    updateTotalPage(initialValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues])

  useEffect(() => {
    // We make sure 'on Init' is called only once
    if (initialized.current && initialized.current !== previousInitialized) {
      onInit(percentProgress, currentPage)
    }
  }, [initialized, previousInitialized, onInit, percentProgress, currentPage])

  const previousCurrentPage = usePrevious(currentPage)
  useEffect(() => {
    if (previousCurrentPage !== undefined && previousCurrentPage !== currentPage) {
      onPageChange?.(currentPage)
    }
  }, [currentPage, onPageChange, previousCurrentPage])

  const handleNextPress = useCallback(
    (setTouched: FormButtonParams['setTouched']) => {
      const errors = setTouched()
      if (errors.length === 0) {
        if (!isLastPage) {
          let nextPage = currentPage + 1
          while (hiddenPages.has(nextPage) && nextPage <= schemaTotalPages) {
            nextPage++
          }
          if (nextPage <= schemaTotalPages) {
            setCurrentPage(nextPage)
          }
        }
        setShowErrorBanner(false)
      } else {
        setShowErrorBanner(true)
      }
      AccessibilityInfo.announceForAccessibility(`Complete ${errors.length} required items to continue`)
      return errors
    },
    [currentPage, hiddenPages, isLastPage, schemaTotalPages],
  )

  const handleBackPress = useCallback(() => {
    if (currentPage > 0) {
      onBackPress?.(currentPage)
    }
    if (currentPage > 0) {
      let prevPage = currentPage - 1
      while (hiddenPages.has(prevPage) && prevPage > 0) {
        prevPage--
      }
      setShowErrorBanner(false)
      setCurrentPage(prevPage)
    }
  }, [currentPage, hiddenPages, onBackPress])

  const FormButton = useCallback(
    ({ handleSubmit, setTouched, loading }: FormButtonParams) => {
      if (isLastPage) {
        return (
          <CTAContainer videoSessionOpen={videoSessionOpen}>
            <PrimaryButton
              aria-label={
                loading
                  ? formatMessage({ defaultMessage: 'Loading', description: 'Button for loading state' })
                  : isProviderDraft
                    ? formatMessage({
                        defaultMessage: 'Save as draft',
                        description: 'Button for Provider to save a draft',
                      })
                    : bypassReview
                      ? bypassReviewButtonText
                      : formatMessage({ defaultMessage: 'Review', description: 'Button to start review' })
              }
              text={
                loading ? (
                  <FormattedMessage defaultMessage='Loading...' description='Button for loading state' />
                ) : isProviderDraft ? (
                  <FormattedMessage defaultMessage='Save as draft' description='Button for Provider to save a draft' />
                ) : bypassReview ? (
                  bypassReviewButtonText
                ) : (
                  <FormattedMessage defaultMessage='Review' description='Button to start review' />
                )
              }
              onPress={() => {
                if (showFullProgressOnSubmit) {
                  const errors = handleNextPress(setTouched)
                  if (errors.length === 0) {
                    setIsSubmitted(true)
                  }
                } else {
                  handleNextPress(setTouched)
                }
                handleSubmit()
              }}
              testID={tID('form-reviewButton')}
              accessibilityLabel={
                loading
                  ? formatMessage({
                      defaultMessage: 'Loading...',
                      description: 'button text loading',
                    })
                  : isProviderDraft
                    ? formatMessage({
                        defaultMessage: 'Save as draft',
                        description: 'Button for Provider to save a draft',
                      })
                    : bypassReview
                      ? bypassReviewButtonText
                      : formatMessage({
                          defaultMessage: 'Review',
                          description: 'button text review',
                        })
              }
              disabled={loading}
              fullWidth
            />
          </CTAContainer>
        )
      } else {
        return (
          <CTAContainer videoSessionOpen={videoSessionOpen}>
            <PrimaryButton
              text={<FormattedMessage defaultMessage='Next' description='Button to go to next screen' />}
              onPress={() => handleNextPress(setTouched)}
              testID={tID('form-nextButton')}
              accessibilityLabel={intl.formatMessage({
                defaultMessage: 'Next',
                description: 'button text next',
              })}
              fullWidth
            />
          </CTAContainer>
        )
      }
    },
    [
      isLastPage,
      videoSessionOpen,
      formatMessage,
      isProviderDraft,
      bypassReview,
      bypassReviewButtonText,
      showFullProgressOnSubmit,
      handleNextPress,
      intl,
    ],
  )

  const updateTotalPage = (answers: ActivityResponseData | Dict | null) => {
    if (answers && schema) {
      const newHiddenPages = getHiddenConditionalPageNumbers(schema, answers)

      if (!isEqual(newHiddenPages, previousAnswers.current)) {
        let adjustedTotalPages = schemaTotalPages
        while (adjustedTotalPages > 0 && newHiddenPages.has(adjustedTotalPages)) {
          adjustedTotalPages--
        }
        setTotalPages(adjustedTotalPages)
        setHiddenPages(newHiddenPages)
        previousAnswers.current = newHiddenPages

        if (currentPage > adjustedTotalPages) {
          setCurrentPage(adjustedTotalPages)
        }

        setAssessmentMaxPage && setAssessmentMaxPage(adjustedTotalPages)
      }
    }
  }

  // on close press open bottom sheet if it's a new entry
  const handleClosePress = () => {
    const formValuesEmpty = isNil(formValues.current)
    Keyboard.dismiss()
    // Drop any queued autosave so it can't fire after we leave
    cancelAutoSave()
    if (newEntry && !formValuesEmpty && withModal) {
      setShowModal(true)
    } else {
      if (!formValuesEmpty && !isProviderDraft) {
        saveForm({ values: formValues.current! })
      }
      exit({ values: !formValuesEmpty ? formValues.current : initialValues })
    }
  }

  const handleSavePress = useCallback(() => {
    if (previousPercentProgress !== percentProgress) {
      onSavePress(percentProgress, currentPage)
    }
    saveForm({ values: formValues.current! })
    setShowModal(false)
    closeBottomSheet(true, ActivityExitType.SAVE_DRAFT)
  }, [closeBottomSheet, currentPage, onSavePress, percentProgress, previousPercentProgress, saveForm])

  const handleDeletePress = useCallback(() => {
    deleteDraft(percentProgress)
    setShowModal(false)
    closeBottomSheet(true, ActivityExitType.EXIT_WITHOUT_SAVING)
  }, [closeBottomSheet, deleteDraft, percentProgress])

  // Save on the first edit (leading edge) so a draft is created right away, then again 3s
  // after the user pauses typing. A new edit resets the trailing timer, so within a burst
  // only the first and final values are sent instead of one request per keystroke.
  const debouncedHandleFormChange = useDebouncedCallback(
    (values) => {
      if (isMounted()) {
        logDiagnostics?.({
          message: 'debouncedHandleFormChange fired (ActivityEntry shared component)',
          timestamp: Date.now(),
        })
        saveForm({ values })
        if (previousPercentProgress !== percentProgress) {
          onSavePress(percentProgress, currentPage)
        }
      }
    },
    AUTOSAVE_DEBOUNCE_MS,
    { leading: true },
  )
  const cancelAutoSave = debouncedHandleFormChange.cancel

  // Drop any queued autosave when the screen loses focus
  useEffect(() => {
    if (!focused) {
      cancelAutoSave()
    }
  }, [focused, cancelAutoSave])

  useEffect(() => {
    setCancelPendingDraftSaves(cancelAutoSave)
  }, [setCancelPendingDraftSaves, cancelAutoSave])

  const handleFormChange = ({ values }: FormChangeProperties) => {
    if (!disableAutoSave) {
      if (focused) {
        debouncedHandleFormChange(values)
      }
      withConditionalLastPages && updateTotalPage(values)
    }
    formValues.current = values
  }

  const handleModalClose = useCallback(() => {
    closeBottomSheet(false)
    setShowModal(false)
  }, [closeBottomSheet])

  const getModal = useCallback(() => {
    return (
      <Modal
        bottomSheetRef={bottomSheetRef}
        snapPoints={[316]}
        onCloseEnd={() => {
          setShowModal(false)
        }}
        modalContents={
          <ActivityExitModalContent
            exitModalContentType={ACTIVITY_EXIT_MODAL_CONTENT.CLOSE_NEW_DRAFT}
            onPrimaryButtonPress={handleSavePress}
            onRequestClose={handleModalClose}
            onTertiaryButtonPress={handleDeletePress}
            modalHasCloseButton={modalHasCloseButton}
          />
        }
        onRequestClose={() => {
          setShowModal(false)
        }}
        visible
      />
    )
  }, [handleSavePress, handleDeletePress, handleModalClose, modalHasCloseButton])

  const calculateScrollFieldHeight = useCallback(() => {
    return windowHeight - (headerHeight + footerHeight)
  }, [footerHeight, headerHeight, windowHeight])

  return (
    <ActivityEntryContent
      testID={tID('ActivityEntry')}
      backgroundColor={backgroundColor}
      videoSessionOpen={videoSessionOpen}
    >
      <ActivityHeader
        a11yFocusedElementOnLoad={a11yFocusedElementOnLoad}
        title={activityTitle}
        percentProgress={isProviderPreview || hideProgressBar ? undefined : percentProgress}
        onBackPress={enableBackNavigation && !singlePageContent && currentPage > 0 ? handleBackPress : undefined}
        onClosePress={handleClosePress}
        status='draft'
        date={newEntry ? null : responseDate}
        setHeaderHeight={IS_WEB ? setHeaderHeight : noop}
        progressBarAccessibilityLiveRegion='assertive'
        isAtTopOfPage={isAtTopOfPage}
        isProviderPreview={isProviderPreview}
      />
      <Flex1View testID={tID('form-container')}>
        {schema && !isNil(initialValues) && (
          <FormBody
            intl={intl}
            schema={schema}
            // Since the FormBody is consuming Contentmetadata and Metadata,
            // we cast this here since we know it is Contentmetadata
            uiSchema={uiSchema}
            instructions={instructions}
            {...(bannerMessage && {
              scrollContainerCustomStyles: {
                formBodyPageContainer: css`
                  padding: 0;
                `,
              },
            })}
            InstructionsSection={InstructionsSection}
            currentPage={currentPage}
            totalPages={totalPages}
            singlePageContent={singlePageContent}
            showCurrentPageOnly={showCurrentPageOnly}
            hiddenPages={hiddenPages}
            initialScrollIndex={editIndex ?? (startFromFirstPage ? 0 : lastPage)}
            suppressInitialScroll={showUpdatePrepopulatedFormBanner}
            name={name}
            saveForm={saveForm}
            formButton={isProviderPreview || hideSubmitButton ? undefined : FormButton}
            onFormChange={handleFormChange}
            initialValues={initialValues}
            inputAccessoryViewID={inputAccessoryViewID}
            showErrorBanner={showErrorBanner}
            withPageBreaks={withPageBreaks}
            setFooterHeight={IS_WEB ? setFooterHeight : noop}
            // Only calculate the scroll field height on web, doing this on mobile will cause slow performance
            scrollListHeight={IS_WEB && !keyboardVisible ? calculateScrollFieldHeight() : undefined}
            locale={locale}
            customerCountryList={customerCountryList}
            userCountryIsoCode={userCountryIsoCode}
            formContext={{
              ...context,
              ...(bannerMessage && {
                insertElementInScrollContainerOnce: (
                  <InlineInfoBanner text={bannerMessage} marginBottom='0' marginTop='8' icon={bannerIcon} />
                ),
              }),
            }}
            onPopOverPress={onPopOverPress}
            useCustomizedActivityIntroduction={useCustomizedActivityIntroduction}
            activityIntroductionProviderInfo={activityIntroductionProviderInfo}
            hasNewLookAndFeel
            backgroundColor={getAuroraAwareBackgroundColor(isAuroraTheme, backgroundColor)}
            setIsAtTopOfPage={setIsAtTopOfPage}
            disableIsRequiredFormValidation={disableIsRequiredFormValidation}
            customErrorMessage={customErrorMessage ? formatMessage(customErrorMessage) : undefined}
            sentryLogMissingUISchemaKeys={sentryLogMissingUISchemaKeys}
            formBodyCustomStyles={
              {
                ...(bypassReview && !maxWidthSubmitButton && { submitButtonWrapper: { maxWidth: 'fit-content' } }),
              } as unknown as FormBodyCustomStyles
            }
          />
        )}
      </Flex1View>
      {withModal && showModal && getModal()}
      <InputAccessory nativeID={inputAccessoryViewID} />
      <ThemedStatusBar defaultStyle='dark' />
    </ActivityEntryContent>
  )
}
